import { httpsCallable } from 'firebase/functions';
import type {
  AdsAdGroupLive,
  AdsAdLive,
  AdsBudgetOrder,
  AdsCampaignLive,
  AdsCustomReport,
  AdsEligibilityRecord,
  AdsGeoLocation,
  AdsKeywordLive,
  AdsMetricRow,
  AdsNegativeKeyword,
  AdsPromotableApp,
  AdsProductPage,
  AdsSupplySource,
  AdsTargetingDimensions,
  AccessibilityCapability,
  AccessibilityDeclarationValues,
  AccessibilityDeviceFamily,
  AgeRatingValues,
  AiGrant,
  BuildRef,
  CustomerReviewEntry,
  GlobalRole,
  KidsAgeBand,
  KeywordCategory,
  KeywordRankStatus,
  KeywordSource,
  Platform,
  ReleaseType,
  StoreGrant,
  UserStatus,
} from '@asm/shared';
import { functions } from './firebase';

/**
 * Typed wrappers for every callable. Names must match functions/src/index.ts exports.
 * The SDK's default deadline is 70s — long-running callables (syncs, pushes, reports)
 * must pass a timeout matching their server budget or the client aborts early with
 * deadline-exceeded while the server keeps working.
 */
function call<I, O>(name: string, timeoutMs?: number) {
  const fn = httpsCallable<I, O>(functions, name, timeoutMs ? { timeout: timeoutMs } : undefined);
  return async (input: I): Promise<O> => (await fn(input)).data;
}

export type BootstrapResult = { status: 'active' | 'disabled' | 'unprovisioned'; reason?: 'domain' };
type Ok = { ok: boolean };

export interface AnalyticsOverviewResult {
  days: number;
  selectedStoreId: string | null;
  selectedAppId: string | null;
  availableStores: Array<{ storeId: string; name: string; color?: string; icon?: string }>;
  availableApps: Array<{
    storeId: string;
    storeName: string;
    appId: string;
    name: string;
    iconUrl: string | null;
    platforms?: string[];
    devices?: string[] | null;
  }>;
  primaryCurrency: string;
  exchangeRateDate: string;
  totals: { proceeds: Record<string, number>; proceedsPrimary: number; downloads: number; units: number };
  growth: { proceeds: number | null; downloads: number | null; hasPrev: boolean };
  series: Array<{ date: string; proceeds: number; downloads: number }>;
  perStore: Array<{
    storeId: string;
    name: string;
    color?: string;
    icon?: string;
    proceeds: Record<string, number>;
    proceedsPrimary: number;
    downloads: number;
    units: number;
    hasFinance: boolean;
    canSync: boolean;
    financeSyncedAt: number | null;
    latestReportDate: string | null;
  }>;
  topApps: Array<{
    storeId: string;
    appId: string;
    name: string;
    iconUrl: string | null;
    platforms?: string[];
    devices?: string[] | null;
    proceeds: number;
    downloads: number;
  }>;
  storesTotal: number;
  storesWithFinance: number;
  appsTotal: number;
}

export type Section<T> = { ok: true; data: T } | { ok: false; error: string };

export interface ReviewDetailData {
  id: string;
  contactFirstName: string;
  contactLastName: string;
  contactPhone: string;
  contactEmail: string;
  demoAccountName: string;
  demoAccountPassword: string;
  demoAccountRequired: boolean;
  notes: string;
  hasDemoPassword: boolean;
  attachments: Array<{ id: string; fileName: string; fileSize: number | null; assetState: string }>;
}

export interface AppExtrasResult {
  versionString: string | null;
  versionEditable: boolean;
  reviewDetail: Section<ReviewDetailData | null>;
  phasedRelease: Section<{ id: string; state: string; currentDayNumber: number | null; startDate: string | null } | null>;
  submission: Section<{
    id: string;
    state: string;
    platform: string;
    submittedDate: string | null;
    items: Array<{ id: string; state: string; itemType: string; versionString: string | null; resourceId: string | null }>;
  } | null>;
  ageRating: Section<(AgeRatingValues & { id: string }) | null>;
  availability: Section<{ availableInNewTerritories: boolean | null; availableTerritories: number; totalTerritories: number }>;
  price: Section<{ baseTerritory: string | null; customerPrice: string | null; proceeds: string | null }>;
  iaps: Section<Array<{ id: string; name: string; productId: string; type: string; state: string }>>;
  subscriptionGroups: Section<Array<{ id: string; name: string; subscriptions: Array<{ id: string; name: string; productId: string; state: string; period: string }> }>>;
  eula: Section<string | null>;
  productPages: Section<Array<{ id: string; name: string; visible: boolean }>>;
  experiments: Section<Array<{ id: string; name: string; state: string; trafficProportion: number | null }>>;
  events: Section<Array<{ id: string; name: string; state: string }>>;
  previewSets: Section<Array<{ id: string; previewType: string; previewCount: number; previews: Array<{ id: string; fileName: string; state: string }> }>>;
  betaGroups: Section<Array<{ id: string; name: string; isInternal: boolean; publicLink: string | null }>>;
  recentBuilds: Section<BuildRef[]>;
  encryption: Section<Array<{ id: string; state: string; usesEncryption: boolean | null; createdDate: string | null }>>;
  accessibility: Section<AccessibilityDeclarationValues[]>;
  nominations: Section<NominationRow[]>;
}

export interface IapConfigResult {
  iap: {
    id: string;
    name: string;
    productId: string;
    type: string;
    state: string;
    reviewNote: string | null;
    familySharable: boolean | null;
  };
  localizations: Array<{ id: string; locale: string; name: string; description: string | null }>;
  price: { customerPrice: string; proceeds: string } | null;
  reviewScreenshot: { id: string; fileName: string; assetState: string } | null;
}

export interface NominationRow {
  id: string;
  name: string;
  type: string;
  description: string;
  state: string;
  publishStartDate: string | null;
  publishEndDate: string | null;
  submittedDate: string | null;
}

export interface AppAvailabilityTerritory {
  id: string;
  territoryCode: string;
  currency: string | null;
  available: boolean;
  releaseDate: string | null;
  preOrderEnabled: boolean;
  preOrderPublishDate: string | null;
  contentStatuses: string[];
}

export interface AppAvailabilityResult {
  id: string;
  availableInNewTerritories: boolean | null;
  territories: AppAvailabilityTerritory[];
}

export interface ReviewScreenshotResult {
  id: string;
  fileName: string;
  fileSize: number | null;
  assetState: string;
  templateUrl: string | null;
  width: number | null;
  height: number | null;
}

export interface SubscriptionConfigResult {
  id: string;
  name: string;
  productId: string;
  state: string;
  period: string;
  familySharable: boolean;
  reviewNote: string;
  localization: { id: string; locale: string; name: string; description: string } | null;
  availability: { id: string | null; availableInNewTerritories: boolean; territoryCodes: string[] };
  territories: Array<{ id: string; currency: string | null }>;
  pricePoints: Array<{ id: string; territory: string; currency: string | null; customerPrice: string; proceeds: string }>;
  prices: Array<{ id: string; territory: string; customerPrice: string | null; startDate: string | null; preserveCurrentPrice: boolean }>;
  introductoryOffers: Array<{
    id: string;
    territory: string;
    duration: string;
    offerMode: string;
    numberOfPeriods: number;
    startDate: string | null;
    endDate: string | null;
  }>;
}

export interface CrashOverviewRow {
  storeId: string;
  storeName: string;
  storeColor: string | null;
  storeIcon: string | null;
  appId: string;
  appName: string;
  iconUrl: string | null;
  platforms: string[];
  crash: {
    state: 'pending-apple' | 'ready' | 'error';
    days: Array<{ date: string; crashes: number }>;
    last7: number;
    prev7: number;
    topVersions: Array<{ version: string; crashes: number }>;
    topDevices: Array<{ device: string; crashes: number }>;
    latestDate: string | null;
    error?: string | null;
    syncedAt: number | null;
  } | null;
  assignment: {
    uid: string;
    email: string;
    name: string | null;
    status: 'open' | 'investigating' | 'resolved';
    note: string;
    by: string;
    at: number | null;
  } | null;
  canAssign: boolean;
}

export interface OverviewRow {
  storeId: string;
  storeName: string;
  storeColor: string | null;
  storeIcon: string | null;
  appId: string;
  appName: string;
  bundleId: string;
  primaryLocale: string;
  iconUrl: string | null;
  platform: string;
  platforms: string[];
  devices: string[] | null;
  hasLive: boolean;
  versionString: string;
  state: string;
  submittedDate: string | null;
  bucket: 'rejected' | 'waiting' | 'inReview' | 'approved' | 'draft' | 'live' | 'none';
}

export interface KeywordOverviewResult {
  app: { id: string; name: string; iconUrl: string | null; locales: string[]; platforms: Platform[] };
  project: {
    storeId: string; appId: string; platform: Platform; enabled: boolean; defaultCountries: string[];
    keywordCount: number; lastRunStatus?: 'success' | 'partial' | 'error'; lastError?: string | null;
    createdAt: number | null; updatedAt: number | null; lastRunAt: number | null;
    comparisonApps: Array<{
      adamId: string; name: string; iconUrl: string | null; bundleId: string | null;
      sellerName: string | null; appStoreUrl: string | null; addedAt: number | null; addedBy: string;
    }>;
  } | null;
  comparisonStores: Record<string, Array<{ id: string; name: string; color: string | null; icon: string | null }>>;
  keywords: Array<{
    id: string; term: string; normalized: string; locale: string; category: KeywordCategory;
    sources: KeywordSource[]; countries: string[]; locked: boolean; archivedAt: number | null;
    createdAt: number | null; updatedAt: number | null;
    currentRanks: Record<string, {
      rank: number | null; previousRank: number | null; bestRank: number | null; resultCount: number;
      status: KeywordRankStatus; checkedAt: number | null; checkedDate: string;
    }>;
  }>;
  history: Array<{ keywordId: string; date: string; country: string; rank: number | null; status: string }>;
  permissions: { manage: boolean; delete: boolean };
}

export interface KeywordIntelligenceResult {
  country: string;
  rows: Array<{
    keywordId: string; term: string; category: KeywordCategory; rank: number | null; previousRank: number | null;
    resultCount: number; popularity: number | null; popularitySource: 'apple-ads' | 'autocomplete' | 'unavailable';
    belowReportingThreshold: boolean; autocompletePriority: number | null; difficulty: number; chance: number;
    kei: number; estimatedInstalls: number; computedAt: number | null;
    popularityHistory: Array<{ date: string; value: number | null }>;
    competitorRanks: Array<{ appId: string; appName: string; iconUrl: string | null; rank: number | null }>;
  }>;
  visibility: Array<{ appId: string; appName: string; points: Array<{ date: string; value: number }> }>;
  competitors: Array<{
    appId: string; appName: string; iconUrl: string | null; shared: number;
    opportunities: Array<{ keywordId: string; term: string; ourRank: number | null; competitorRank: number | null }>;
  }>;
  summary: { visibility: number; estimatedInstalls: number; averageChance: number; reportedPopularity: number };
  settings: { assumedConversion: number; appleAdsAccounts: Array<{ id: string; label: string }>; lastComputedAt: number | null };
}

export interface KeywordDiscoveryResult {
  country: string;
  suggestions: Array<{
    term: string; normalized: string; sources: string[]; tracked: boolean; popularity: number | null;
    autocompletePriority: number | null; ownRank: number | null; bestCompetitorRank: number | null;
    opportunity: boolean; difficulty: number; chance: number; kei: number;
  }>;
  generatedAt: number;
}

export const api = {
  authBootstrap: call<{ device?: string }, BootstrapResult>('authBootstrap'),
  appsOverview: call<Record<string, never>, { rows: OverviewRow[] }>('appsOverview'),
  keywordOverview: call<
    { storeId: string; appId: string; platform: Platform; includeArchived?: boolean }, KeywordOverviewResult
  >('keywordOverview', 130_000),
  keywordAppSearch: call<
    { storeId: string; appId: string; platform: Platform; search: string; country: string },
    { apps: Array<{ adamId: string; name: string; iconUrl: string | null; bundleId: string | null; sellerName: string | null; appStoreUrl: string | null }> }
  >('keywordAppSearch', 130_000),
  keywordComparisonAppAdd: call<
    { storeId: string; appId: string; platform: Platform; app: { adamId: string; name: string; iconUrl: string | null; bundleId: string | null; sellerName: string | null; appStoreUrl: string | null } }, Ok
  >('keywordComparisonAppAdd'),
  keywordComparisonAppRemove: call<
    { storeId: string; appId: string; platform: Platform; adamId: string }, Ok
  >('keywordComparisonAppRemove'),
  keywordSettingsUpdate: call<
    { storeId: string; appId: string; platform: Platform; enabled: boolean; defaultCountries: string[] }, Ok
  >('keywordSettingsUpdate'),
  keywordUpsertBulk: call<
    {
      storeId: string; appId: string; platform: Platform; terms: string[]; locale?: string; countries: string[];
      source?: KeywordSource; category?: KeywordCategory; locked?: boolean;
    }, { added: number; updated: number; skipped: number; keywordCount: number }
  >('keywordUpsertBulk', 130_000),
  keywordLockSet: call<
    { storeId: string; appId: string; platform: Platform; keywordId: string; locked: boolean }, Ok
  >('keywordLockSet'),
  keywordArchiveSet: call<
    { storeId: string; appId: string; platform: Platform; keywordId: string; archived: boolean }, Ok
  >('keywordArchiveSet'),
  keywordDelete: call<
    { storeId: string; appId: string; platform: Platform; keywordId: string }, Ok
  >('keywordDelete', 130_000),
  keywordDeleteBulk: call<
    { storeId: string; appId: string; platform: Platform; keywordIds: string[] }, { deleted: number; skipped: number }
  >('keywordDeleteBulk', 190_000),
  keywordImportPreview: call<
    { storeId: string; appId: string; platform: Platform },
    {
      suggestions: Array<{ term: string; locale: string; source: KeywordSource; category: KeywordCategory }>;
      combinations: Array<{ term: string; byteLength: number; metadataSafe: boolean; confidence: 'high' | 'medium' | 'experimental' }>;
    }
  >('keywordImportPreview', 130_000),
  keywordHistory: call<
    { storeId: string; appId: string; platform: Platform; keywordId: string; countries: string[]; targetAppIds?: string[]; days?: number },
    { keywordId: string; points: Array<{ date: string; country: string; targetAppId: string; rank: number | null; status: string }> }
  >('keywordHistory', 130_000),
  keywordCheckNow: call<
    { storeId: string; appId: string; platform: Platform; keywordIds?: string[]; countries?: string[] },
    { checked: number; failed: number; deferred: number; freshQueries: number; cachedQueries: number; remaining: number; status: 'success' | 'partial' }
  >('keywordCheckNow', 550_000),
  keywordSuggestions: call<
    { query?: string; limit?: number },
    { suggestions: Array<{ term: string; normalized: string; sources: string[]; usageCount: number; lastUsedAt: number | null }> }
  >('keywordSuggestions'),
  keywordLiveSearch: call<
    { storeId: string; appId: string; platform: Platform; term: string; country: string; targetAppId?: string },
    {
      term: string; country: string; targetAppId: string; resultCount: number; yourRank: number | null; checkedAt: number;
      apps: Array<{ rank: number; adamId: string; name: string; iconUrl: string | null; sellerName: string | null; rating: number | null; ratingCount: number | null; isTarget: boolean }>;
    }
  >('keywordLiveSearch', 30_000),
  keywordIntelligenceOverview: call<
    { storeId: string; appId: string; platform: Platform; country: string; days?: number }, KeywordIntelligenceResult
  >('keywordIntelligenceOverview', 130_000),
  keywordIntelligenceRefresh: call<
    { storeId: string; appId: string; platform: Platform; countries: string[] },
    { keywords: number; countries: number; reported: number; hinted: number; appleAdsAccounts: number }
  >('keywordIntelligenceRefresh', 550_000),
  keywordDiscoveryRun: call<
    { storeId: string; appId: string; platform: Platform; country: string; seeds?: string[] }, KeywordDiscoveryResult
  >('keywordDiscoveryRun', 190_000),
  keywordIntelligenceSettingsUpdate: call<
    { storeId: string; appId: string; platform: Platform; assumedConversion: number }, Ok
  >('keywordIntelligenceSettingsUpdate'),
  asoMetadataCoverage: call<
    { storeId: string; appId: string; platform: Platform },
    {
      primaryLocale: string; total: number;
      locales: Array<{
        locale: string; name: string; subtitle: string; keywords: string;
        keywordBytes: number; keywordByteLimit: number; coveredCount: number; total: number;
        coverage: Array<{ term: string; inName: boolean; inSubtitle: boolean; inKeywords: boolean; covered: boolean }>;
      }>;
    }
  >('asoMetadataCoverage', 130_000),

  teamList: call<
    Record<string, never>,
    {
      members: Array<{
        uid: string;
        name: string;
        email: string;
        photoUrl: string | null;
        status: UserStatus;
        grants: Record<string, StoreGrant>;
        ai: { features: { translate: boolean; generate: boolean }; monthlyCredits: number };
      }>;
      invites: Array<{ email: string; grants: Record<string, StoreGrant> }>;
      stores: Array<{ storeId: string; name: string; color?: string; icon?: string; apps: Array<{ id: string; name: string; iconUrl: string | null }> }>;
    }
  >('teamList'),
  usersInvite: call<{ email: string; role: GlobalRole; grants?: Record<string, StoreGrant>; ai?: AiGrant }, Ok>('usersInvite'),
  usersUpdate: call<{ uid: string; role?: GlobalRole; grants?: Record<string, StoreGrant>; ai?: AiGrant; reactivate?: boolean }, Ok>('usersUpdate'),
  usersSetStatus: call<{ uid: string; status: UserStatus }, Ok>('usersSetStatus'),
  allowlistUpdate: call<{ email: string; role?: GlobalRole; grants?: Record<string, StoreGrant>; ai?: AiGrant }, Ok>('allowlistUpdate'),
  allowlistRemove: call<{ email: string }, Ok>('allowlistRemove'),
  accessRequestResolve: call<{ uid: string; approve: boolean; role?: GlobalRole; grants?: Record<string, StoreGrant>; ai?: AiGrant }, Ok>('accessRequestResolve'),

  storesAdd: call<
    {
      name: string;
      mock?: boolean;
      color?: string;
      icon?: string;
      vendorNumber?: string;
      creds?: { issuerId: string; keyId: string; p8: string };
    },
    { storeId: string; appsCount: number }
  >('storesAdd'),
  storesTest: call<{ storeId: string }, { ok: boolean; appsCount: number }>('storesTest'),
  storesUpdateKey: call<{ storeId: string; creds: { issuerId: string; keyId: string; p8: string } }, Ok>('storesUpdateKey'),
  storesGetKeyMeta: call<{ storeId: string }, { hasKey: boolean; issuerId: string; keyId: string }>('storesGetKeyMeta'),
  storesRename: call<
    { storeId: string; name?: string; color?: string; icon?: string; vendorNumber?: string | null },
    Ok
  >('storesRename'),
  storesRecolor: call<Record<string, never>, { recolored: number }>('storesRecolor'),
  bundleIdsList: call<
    { storeId: string },
    { bundleIds: Array<{ id: string; identifier: string; name: string; platform: string; seedId: string }> }
  >('bundleIdsList'),
  bundleIdCreate: call<
    { storeId: string; identifier: string; name: string; platform: 'IOS' | 'MAC_OS' | 'UNIVERSAL' },
    { bundleId: { id: string; identifier: string } }
  >('bundleIdCreate'),
  bundleIdDelete: call<{ storeId: string; bundleIdId: string; identifier?: string }, Ok>('bundleIdDelete'),
  financeSync: call<{ storeId: string; days?: number }, { fetched: number }>('financeSync', 310_000),
  analyticsOverview: call<{ days?: number; sync?: boolean; syncStoreIds?: string[]; storeId?: string | null; appId?: string | null }, AnalyticsOverviewResult>('analyticsOverview', 550_000),
  storesDelete: call<{ storeId: string }, Ok>('storesDelete'),

  storesSync: call<{ storeId: string }, { skipped: boolean; apps?: number; reason?: string }>('storesSync', 550_000),
  storesHardSync: call<{ storeId: string }, { skipped: boolean; apps?: number; deepSynced?: number; failed?: number; financeDays?: number; reason?: string }>('storesHardSync', 550_000),
  storeProxySet: call<{ storeId: string; enabled: boolean; country?: string; rotate?: boolean }, Ok>('storeProxySet'),
  storeProxyTest: call<
    { storeId: string },
    { ip: string; proxied: boolean; countryCode: string | null; country: string | null }
  >('storeProxyTest', 70_000),
  webhooksEnable: call<{ storeId: string }, { apps: number }>('webhooksEnable', 320_000),
  webhooksDisable: call<{ storeId: string }, Ok>('webhooksDisable', 320_000),
  appsSyncOne: call<{ storeId: string; appId: string }, { skipped: boolean; locales?: number; reason?: string }>('appsSyncOne', 310_000),

  locPush: call<
    { storeId: string; appId: string; platform?: string; locales: string[] },
    {
      results: Array<{
        locale: string;
        ok: boolean;
        pushedKeys: string[];
        error?: string;
        /** One-field rejections (duplicate App Name) — the rest of the locale pushed. */
        fieldErrors?: Array<{ key: string; code: 'name-taken'; error: string }>;
      }>;
      summary: string;
    }
  >('locPush', 550_000),
  locAddLanguage: call<
    { storeId: string; appId: string; platform?: string; locales: string[]; copyFrom?: string | null },
    {
      added: string[];
      skipped: string[];
      warnings: Array<{ locale: string; error: string }>;
      failed: Array<{ locale: string; error: string }>;
    }
  >('locAddLanguage', 310_000),
  locRemoveLanguage: call<
    { storeId: string; appId: string; platform?: string; locale?: string; locales?: string[] },
    {
      ok: boolean;
      removedFromLive: boolean;
      removed: string[];
      retainedLive: string[];
      failed: Array<{ locale: string; error: string }>;
    }
  >('locRemoveLanguage', 310_000),
  versionsCreate: call<
    { storeId: string; appId: string; platform?: string; versionString: string },
    { versionId: string; versionString: string }
  >('versionsCreate', 310_000),
  versionsUpdate: call<
    { storeId: string; appId: string; platform?: string; versionString: string },
    { versionId: string; versionString: string }
  >('versionsUpdate'),
  versionInfoUpdate: call<
    {
      storeId: string;
      appId: string;
      platform?: string;
      copyright?: string;
      releaseType?: ReleaseType;
      earliestReleaseDate?: string | null;
      buildId?: string | null;
    },
    { ok: boolean }
  >('versionInfoUpdate'),

  screenshotsSyncLocale: call<
    { storeId: string; appId: string; platform?: string; locale: string },
    { sets: number }
  >('screenshotsSyncLocale', 310_000),
  screenshotsSyncAll: call<
    { storeId: string; appId: string; platform?: string },
    { branch: 'editable' | 'live'; localesSynced: number; sets: number }
  >('screenshotsSyncAll', 310_000),
  screenshotsUpload: call<
    { storeId: string; appId: string; platform?: string; locale: string; displayType: string; storagePath: string; fileName: string },
    { screenshotId: string; state: string }
  >('screenshotsUpload', 310_000),
  screenshotsPollState: call<
    { storeId: string; appId: string; platform?: string; locale: string; displayType: string; screenshotId: string },
    { state: string }
  >('screenshotsPollState'),
  screenshotsDelete: call<
    { storeId: string; appId: string; platform?: string; locale: string; displayType: string; screenshotId: string },
    Ok
  >('screenshotsDelete'),
  screenshotsReorder: call<
    { storeId: string; appId: string; platform?: string; locale: string; displayType: string; orderedIds: string[] },
    Ok
  >('screenshotsReorder'),
  screenshotSetsDelete: call<
    { storeId: string; appId: string; platform?: string; locale: string; displayType: string },
    Ok
  >('screenshotSetsDelete'),
  screenshotDisplayTypeDelete: call<
    { storeId: string; appId: string; platform?: string; displayType: string },
    { deleted: number; failed: number }
  >('screenshotDisplayTypeDelete', 310_000),

  appExtrasGet: call<{ storeId: string; appId: string; platform?: string }, AppExtrasResult>('appExtrasGet', 130_000),
  accessibilitySave: call<
    {
      storeId: string;
      appId: string;
      platform?: string;
      deviceFamily: AccessibilityDeviceFamily;
      capabilities: Partial<Record<AccessibilityCapability, boolean>>;
      publish?: boolean;
    },
    { declaration: AccessibilityDeclarationValues }
  >('accessibilitySave'),
  nominationCreate: call<
    {
      storeId: string;
      appId: string;
      platform?: string;
      name: string;
      type: 'APP_LAUNCH' | 'APP_ENHANCEMENTS' | 'NEW_CONTENT';
      description: string;
      publishStartDate: string;
      publishEndDate?: string;
      notes?: string;
      submit?: boolean;
    },
    { nomination: NominationRow }
  >('nominationCreate'),
  nominationDelete: call<{ storeId: string; appId: string; platform?: string; nominationId: string }, Ok>('nominationDelete'),
  previewUpload: call<
    { storeId: string; appId: string; platform?: string; previewType: string; storagePath: string; fileName: string; locale?: string },
    { previewId: string; state: string }
  >('previewUpload', 550_000),
  previewDelete: call<{ storeId: string; appId: string; platform?: string; previewId: string }, Ok>('previewDelete'),
  iapCreate: call<
    { storeId: string; appId: string; platform?: string; name: string; productId: string; type: 'CONSUMABLE' | 'NON_CONSUMABLE' | 'NON_RENEWING_SUBSCRIPTION' },
    { iapId: string }
  >('iapCreate'),
  iapConfigGet: call<{ storeId: string; appId: string; platform?: string; iapId: string }, IapConfigResult>('iapConfigGet'),
  iapMetadataSave: call<
    {
      storeId: string;
      appId: string;
      platform?: string;
      iapId: string;
      name: string;
      reviewNote?: string;
      familySharable?: boolean;
      localizations?: Array<{ locale: string; name: string; description?: string }>;
    },
    Ok
  >('iapMetadataSave'),
  iapPricePointsList: call<
    { storeId: string; appId: string; platform?: string; iapId: string },
    { pricePoints: Array<{ id: string; customerPrice: string; proceeds: string }> }
  >('iapPricePointsList'),
  iapPriceSet: call<{ storeId: string; appId: string; platform?: string; iapId: string; pricePointId: string }, Ok>('iapPriceSet'),
  iapReviewScreenshotUpload: call<
    { storeId: string; appId: string; platform?: string; iapId: string; storagePath: string; fileName: string },
    { screenshotId: string; state: string }
  >('iapReviewScreenshotUpload', 310_000),
  iapSubmit: call<{ storeId: string; appId: string; platform?: string; iapId: string }, Ok>('iapSubmit'),
  appAvailabilityGet: call<
    { storeId: string; appId: string; platform?: string },
    AppAvailabilityResult | null
  >('appAvailabilityGet'),
  appAvailabilityUpdate: call<
    { storeId: string; appId: string; platform?: string; changes: Array<{ id: string; available: boolean }> },
    { updated: number; failed: Array<{ id: string; message: string }> }
  >('appAvailabilityUpdate', 130_000),
  reviewDetailSave: call<
    {
      storeId: string;
      appId: string;
      platform?: string;
      contactFirstName?: string;
      contactLastName?: string;
      contactPhone?: string;
      contactEmail?: string;
      demoAccountName?: string;
      demoAccountPassword?: string;
      demoAccountRequired?: boolean;
      notes?: string;
    },
    Ok
  >('reviewDetailSave'),
  reviewAttachmentUpload: call<
    { storeId: string; appId: string; platform?: string; storagePath: string; fileName: string },
    { attachmentId: string; state: string }
  >('reviewAttachmentUpload', 310_000),
  reviewAttachmentDelete: call<
    { storeId: string; appId: string; platform?: string; attachmentId: string },
    Ok
  >('reviewAttachmentDelete'),
  phasedReleaseSet: call<
    { storeId: string; appId: string; platform?: string; action: 'enable' | 'pause' | 'resume' | 'complete' | 'disable' },
    { state: string | null }
  >('phasedReleaseSet'),
  testflightTestersList: call<
    { storeId: string; appId: string; platform?: string; groupId: string },
    { testers: Array<{ id: string; email: string; firstName: string; lastName: string; inviteType: string }> }
  >('testflightTestersList'),
  testflightTesterAdd: call<
    { storeId: string; appId: string; platform?: string; groupId: string; email: string; firstName?: string; lastName?: string },
    { tester: { id: string; email: string } }
  >('testflightTesterAdd'),
  testflightTesterRemove: call<
    { storeId: string; appId: string; platform?: string; groupId: string; testerId: string; email?: string },
    Ok
  >('testflightTesterRemove'),
  subscriptionGroupCreate: call<
    { storeId: string; appId: string; platform?: string; referenceName: string },
    { group: { id: string; name: string } }
  >('subscriptionGroupCreate'),
  subscriptionCreate: call<
    { storeId: string; appId: string; platform?: string; groupId: string; name: string; productId: string; period: string; displayName: string; description?: string },
    { subscription: { id: string; name: string; productId: string; state: string; period: string }; existing: boolean; localizationWarning: string | null }
  >('subscriptionCreate'),
  subscriptionConfigGet: call<
    { storeId: string; appId: string; platform?: string; subscriptionId: string },
    { config: SubscriptionConfigResult; reviewScreenshot: ReviewScreenshotResult | null }
  >('subscriptionConfigGet', 130_000),
  subscriptionReviewScreenshotUpload: call<
    { storeId: string; appId: string; platform?: string; subscriptionId: string; storagePath: string; fileName: string },
    { screenshotId: string; state: string }
  >('subscriptionReviewScreenshotUpload', 300_000),
  subscriptionReviewScreenshotDelete: call<
    { storeId: string; appId: string; platform?: string; subscriptionId: string; screenshotId: string },
    Ok
  >('subscriptionReviewScreenshotDelete', 100_000),
  subscriptionMetadataSave: call<
    {
      storeId: string; appId: string; platform?: string; subscriptionId: string; name: string; period: string;
      familySharable: boolean; reviewNote: string; displayName: string; description: string;
    },
    Ok
  >('subscriptionMetadataSave', 100_000),
  subscriptionAvailabilitySave: call<
    {
      storeId: string; appId: string; platform?: string; subscriptionId: string; planAvailabilityId: string | null;
      territoryCodes: string[]; availableInNewTerritories: boolean;
    },
    Ok
  >('subscriptionAvailabilitySave', 130_000),
  subscriptionPricesSet: call<
    {
      storeId: string; appId: string; platform?: string; subscriptionId: string; pricePointId: string;
      applyEqualized: boolean; territoryCodes: string[];
    },
    { updated: number; failed: string[] }
  >('subscriptionPricesSet', 550_000),
  subscriptionFreeTrialCreate: call<
    {
      storeId: string; appId: string; platform?: string; subscriptionId: string; territoryCode: string;
      duration: string; startDate: string | null; endDate: string | null;
    },
    Ok
  >('subscriptionFreeTrialCreate', 100_000),
  subscriptionFreeTrialDelete: call<
    { storeId: string; appId: string; platform?: string; subscriptionId: string; offerId: string },
    Ok
  >('subscriptionFreeTrialDelete'),
  subscriptionSubmit: call<
    { storeId: string; appId: string; platform?: string; subscriptionId: string; name?: string },
    Ok
  >('subscriptionSubmit'),
  pricePointsList: call<
    { storeId: string; appId: string; platform?: string },
    { pricePoints: Array<{ id: string; customerPrice: string; proceeds: string }> }
  >('pricePointsList'),
  priceScheduleSet: call<
    { storeId: string; appId: string; platform?: string; pricePointId: string; customerPrice?: string },
    Ok
  >('priceScheduleSet'),
  ageRatingSave: call<
    { storeId: string; appId: string; platform?: string; levels: Record<string, string>; booleans: Record<string, boolean>; kidsAgeBand: KidsAgeBand | null },
    Ok
  >('ageRatingSave'),
  customerReviewsList: call<
    { storeId: string; appId: string; platform?: string; limit?: number },
    { reviews: CustomerReviewEntry[] }
  >('customerReviewsList'),
  customerReviewRespond: call<
    { storeId: string; appId: string; platform?: string; reviewId: string; body: string },
    Ok
  >('customerReviewRespond'),
  customerReviewResponseDelete: call<
    { storeId: string; appId: string; platform?: string; responseId: string },
    Ok
  >('customerReviewResponseDelete'),

  aiTranslate: call<
    {
      storeId: string;
      appId: string;
      platform?: string;
      sourceLocale: string;
      targetLocales: string[];
      fields: string[];
      overwrite?: boolean;
    },
    { results: Array<{ locale: string; ok: boolean; fieldsWritten: number; error?: string }>; cancelled: boolean }
  >('aiTranslate', 550_000),
  aiTranslationCancel: call<
    { operationId: string },
    { ok: boolean; alreadyFinished: boolean }
  >('aiTranslationCancel'),
  locFillFromPrimary: call<
    { storeId: string; appId: string; platform?: string; mode: 'missing' | 'all' },
    { fieldsWritten: number; localesChanged: number; skippedSourceFields: string[] }
  >('locFillFromPrimary', 130_000),
  aiGenerate: call<
    { storeId: string; appId: string; platform?: string; locale: string; kind: 'name' | 'keywords' | 'subtitle' | 'improve-description' | 'promotional-text' | 'whatsnew'; context?: string | null },
    { options: string[]; field: string; fieldKey: string }
  >('aiGenerate'),
  aiReviewReply: call<
    { storeId: string; appId: string; platform?: string; reviewId: string; attempt?: number },
    { reply: string }
  >('aiReviewReply'),

  maintenanceSet: call<{ enabled: boolean; message?: string }, Ok>('maintenanceSet'),
  settingsUpdate: call<
    {
      aiModel?: string;
      idleTimeoutMinutes?: number | null;
      allowedDomains?: string[];
      reportEmails?: string[];
      reportHour?: number;
      keywordReportEmails?: string[];
      keywordReportHour?: number;
    },
    Ok
  >('settingsUpdate'),
  reportSendNow: call<Record<string, never>, { summary: string }>('reportSendNow', 550_000),
  keywordReportSendNow: call<Record<string, never>, { summary: string }>('keywordReportSendNow', 130_000),
  keywordReportTargets: call<
    Record<string, never>,
    {
      stores: Array<{
        storeId: string;
        name: string;
        color: string | null;
        icon: string | null;
        included: boolean;
        apps: Array<{
          appId: string;
          name: string;
          iconUrl: string | null;
          platforms: string[];
          keywordCount: number;
          included: boolean;
        }>;
      }>;
    }
  >('keywordReportTargets'),
  keywordReportTargetSet: call<{ storeId: string; appId?: string; included: boolean }, Ok>('keywordReportTargetSet'),

  crashesSync: call<
    { storeId: string; force?: boolean },
    { synced: number; pending: number; failed: number; skipped: boolean }
  >('crashesSync', 550_000),
  crashesOverview: call<Record<string, never>, { rows: CrashOverviewRow[] }>('crashesOverview'),
  crashAssignSet: call<
    { storeId: string; appId: string; uid: string | null; status?: 'open' | 'investigating' | 'resolved'; note?: string },
    Ok
  >('crashAssignSet'),

  adsAppleConnect: call<
    { label: string; clientId: string; teamId: string; keyId: string; privateKey: string; orgId: number },
    { accountId: string; campaignsCount: number }
  >('adsAppleConnect'),
  admobConnect: call<
    { label: string; clientId?: string; clientSecret?: string; code: string; redirectUri: string },
    { accountId: string; publisherId: string; currencyCode: string }
  >('admobConnect'),
  admobOauthStatus: call<Record<string, never>, { configured: boolean; clientId?: string }>('admobOauthStatus'),
  adsAccountRemove: call<{ accountId: string }, Ok>('adsAccountRemove'),
  adsCampaignsList: call<Record<string, never>, { campaigns: AdsCampaignLive[]; errors: string[] }>('adsCampaignsList', 130_000),
  adsCampaignSetStatus: call<
    { accountId: string; campaignId: string; status: 'ENABLED' | 'PAUSED' },
    { ok: boolean; status: string }
  >('adsCampaignSetStatus'),
  adsSync: call<{ days?: number }, { days: number; providers: string[] }>('adsSync', 310_000),

  // Apple Search Ads management
  adsAdGroupsList: call<{ accountId: string; campaignId: string; days?: number }, { adGroups: AdsAdGroupLive[] }>('adsAdGroupsList', 130_000),
  adsAdGroupCreate: call<
    {
      accountId: string; campaignId: string; name: string; defaultBid?: Money; cpaGoal?: Money;
      startTime?: string; endTime?: string; automatedKeywordsOptIn?: boolean;
      automatedKeywordsRequired?: boolean; targetingDimensions?: AdsTargetingDimensions;
    },
    { adGroup: AdsAdGroupLive }
  >('adsAdGroupCreate'),
  adsAdGroupUpdate: call<
    {
      accountId: string; campaignId: string; adGroupId: string; name?: string; status?: 'ENABLED' | 'PAUSED';
      defaultBid?: Money; cpaGoal?: Money | null; startTime?: string; endTime?: string | null;
      automatedKeywordsOptIn?: boolean; targetingDimensions?: AdsTargetingDimensions;
    },
    Ok
  >('adsAdGroupUpdate'),
  adsAdGroupDelete: call<{ accountId: string; campaignId: string; adGroupId: string }, Ok>('adsAdGroupDelete'),
  adsKeywordsList: call<{ accountId: string; campaignId: string; adGroupId: string; days?: number }, { keywords: AdsKeywordLive[] }>('adsKeywordsList', 130_000),
  adsKeywordRanks: call<
    { adamId: number; country: string; terms: string[] },
    { ranks: Array<{ term: string; rank: number | null; results: number }>; checkedAt: number }
  >('adsKeywordRanks', 130_000),
  adsKeywordsCreate: call<
    { accountId: string; campaignId: string; adGroupId: string; keywords: Array<{ text: string; matchType: 'EXACT' | 'BROAD'; bid: Money }> },
    { created: number; skipped: number }
  >('adsKeywordsCreate'),
  adsKeywordUpdate: call<
    { accountId: string; campaignId: string; adGroupId: string; keywordId: string; status?: 'ACTIVE' | 'PAUSED'; bid?: Money },
    Ok
  >('adsKeywordUpdate'),
  adsKeywordDelete: call<{ accountId: string; campaignId: string; adGroupId: string; keywordId: string }, Ok>('adsKeywordDelete'),
  adsNegativeKeywordsList: call<{ accountId: string; campaignId: string; adGroupId: string }, { negatives: AdsNegativeKeyword[] }>('adsNegativeKeywordsList'),
  adsNegativeKeywordsAdd: call<
    { accountId: string; campaignId: string; adGroupId: string; keywords: Array<{ text: string; matchType: 'EXACT' | 'BROAD' }> },
    Ok
  >('adsNegativeKeywordsAdd'),
  adsNegativeKeywordDelete: call<{ accountId: string; campaignId: string; adGroupId: string; keywordId: string }, Ok>('adsNegativeKeywordDelete'),
  adsCampaignNegativeKeywordsList: call<{ accountId: string; campaignId: string }, { negatives: AdsNegativeKeyword[] }>('adsCampaignNegativeKeywordsList'),
  adsCampaignNegativeKeywordsAdd: call<
    { accountId: string; campaignId: string; keywords: Array<{ text: string; matchType: 'EXACT' | 'BROAD' }> }, Ok
  >('adsCampaignNegativeKeywordsAdd'),
  adsCampaignNegativeKeywordDelete: call<
    { accountId: string; campaignId: string; keywordId: string }, Ok
  >('adsCampaignNegativeKeywordDelete'),
  adsSearchTermsList: call<{ accountId: string; campaignId: string; adGroupId: string; days?: number }, { terms: AdsMetricRow[] }>('adsSearchTermsList', 130_000),
  adsCampaignSetup: call<{ accountId: string }, { apps: AdsPromotableApp[]; currency: string | null; adsListChecked: boolean }>('adsCampaignSetup', 70_000),
  adsCampaignCreate: call<
    {
      accountId: string; name: string; adamId: number; currency: string; budget: number; dailyBudget: number;
      countries: string[]; supplySources?: AdsSupplySource[]; biddingStrategy?: 'MANUAL_CPT' | 'MAX_CONVERSIONS';
      targetCpa?: number; startTime?: string; endTime?: string;
    },
    { campaign: AdsCampaignLive }
  >('adsCampaignCreate', 130_000),
  adsCampaignUpdate: call<
    {
      accountId: string; campaignId: string; name?: string; status?: 'ENABLED' | 'PAUSED'; currency: string;
      dailyBudget?: number; countries?: string[]; biddingStrategy?: 'MANUAL_CPT' | 'MAX_CONVERSIONS';
      targetCpa?: number | null; startTime?: string; endTime?: string | null;
    },
    Ok
  >('adsCampaignUpdate'),
  adsCampaignDelete: call<{ accountId: string; campaignId: string }, Ok>('adsCampaignDelete'),
  adsAppEligibility: call<
    { accountId: string; adamId: number; countries: string[]; supplySource: AdsSupplySource },
    { records: AdsEligibilityRecord[] }
  >('adsAppEligibility', 130_000),
  adsAppDetails: call<
    { accountId: string; adamId: number },
    { app: { adamId: number; appName: string; artistName?: string; primaryLanguage?: string; deviceClasses?: string[]; iconPictureUrl?: string; availableStorefronts?: string[] } }
  >('adsAppDetails'),
  adsGeoSearch: call<
    { accountId: string; country: string; query: string; entity?: 'AdminArea' | 'Locality' },
    { locations: AdsGeoLocation[] }
  >('adsGeoSearch'),
  adsProductPagesList: call<{ accountId: string; adamId: number }, { productPages: AdsProductPage[] }>('adsProductPagesList'),
  adsAdsList: call<
    { accountId: string; campaignId: string; adGroupId: string; days?: number }, { ads: AdsAdLive[] }
  >('adsAdsList', 130_000),
  adsCustomProductPageAssign: call<
    { accountId: string; campaignId: string; adGroupId: string; adamId: number; productPageId: string; name: string },
    { creative: { id: string; state: string; stateReasons?: string[] }; ad: AdsAdLive }
  >('adsCustomProductPageAssign', 130_000),
  adsAdUpdate: call<
    { accountId: string; campaignId: string; adGroupId: string; adId: string; name?: string; status?: 'ENABLED' | 'PAUSED' }, Ok
  >('adsAdUpdate'),
  adsAdDelete: call<{ accountId: string; campaignId: string; adGroupId: string; adId: string }, Ok>('adsAdDelete'),
  adsImpressionShareList: call<{ accountId: string }, { reports: AdsCustomReport[] }>('adsImpressionShareList'),
  adsImpressionShareCreate: call<
    { accountId: string; name: string; adamId: number; countries: string[]; startTime: string; endTime: string },
    { report: AdsCustomReport }
  >('adsImpressionShareCreate'),
  adsBudgetOrdersList: call<{ accountId: string }, { orders: AdsBudgetOrder[] }>('adsBudgetOrdersList'),
  adsBudgetOrderCreate: call<BudgetOrderInput, { order: AdsBudgetOrder }>('adsBudgetOrderCreate'),
  adsBudgetOrderUpdate: call<BudgetOrderInput & { budgetOrderId: string }, { order: AdsBudgetOrder }>('adsBudgetOrderUpdate'),
};

type Money = { amount: number; currency: string };
type BudgetOrderInput = {
  accountId: string; name: string; startDate: string; endDate: string; budget: Money; orderNumber: string;
  clientName: string; primaryBuyerName: string; primaryBuyerEmail: string; billingEmail: string;
};

/** Extract a human-readable message from a callable error. */
export function callableMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
    // firebase HttpsError messages are already user-facing (our wrap.ts guarantees it)
    return err.message.replace(/^Firebase: /, '').replace(/ \(functions\/[a-z-]+\)\.?$/, '');
  }
  return 'Something went wrong.';
}
